/**
 * The customer profile this bundle was built for — the one place base code
 * reads a profile. `@customer` resolves to customers/<A8_CUSTOMER>/ at build
 * time (vite.config.ts, bin/typecheck.mjs); nothing else in src/ imports
 * from customers/.
 */
import type { CustomerProfile } from "./profile";
import { pagesByName, withApps } from "./profile";
import customer from "@customer/customer";
import type { PageModule } from "../page-dsl/spec";
import type { NavItem } from "../components/navigation/nav-items";
import { DEFAULT_POST_LOGIN_REDIRECT } from "../routes/paths";
import { getPreferences, isInAppPath } from "../services/user-preferences";

/**
 * The profile with its apps: every customers/<id>/apps/<name>/ (an app from continuum-customers
 * base, FEATURE-20261030-app-pages-in-profile-build) adds its pages/*.page.ts and its nav.ts
 * entries, with no edit to the profile's customer.ts. The profile's own page of the same name wins.
 */
export const profile: CustomerProfile = withApps(
  customer,
  pagesByName(import.meta.glob<PageModule>("@customer/apps/*/pages/*.page.ts")),
  Object.values(import.meta.glob<{ default: NavItem[] }>("@customer/apps/*/nav.ts", { eager: true })).flatMap(
    (m) => m.default,
  ),
);

/** where a user lands when they chose nothing: the profile's home, else the base default */
export function defaultPostLoginRedirect(): string {
  return profile.home ?? DEFAULT_POST_LOGIN_REDIRECT;
}

/**
 * where a user lands after login: the page they chose on /app/profile, else
 * the default. Reads the loaded preferences — callers that run right after
 * login await loadPreferences() first.
 */
export function postLoginRedirect(): string {
  const chosen = getPreferences().loginRedirectUrl;
  return chosen && isInAppPath(chosen) ? chosen : defaultPostLoginRedirect();
}
